/**
 * Email address, MIME, and provider payload parsing helpers.
 *
 * @module
 */

import type { CapturedEmail } from './types.js';

export interface ParsedAddress {
  email: string;
  name?: string;
}

export interface ParsedRawMime {
  from: ParsedAddress | null;
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string | null;
  text: string | null;
  html: string | null;
}

type JsonObject = Record<string, unknown>;

/**
 * Parse a single address from a string ("Name <a@b.com>" / "a@b.com") or an
 * object ({ email, name }, as SendGrid uses).
 */
export function parseAddress(input: unknown): ParsedAddress | null {
  if (input && typeof input === 'object' && 'email' in input) {
    const email = String((input as { email: unknown }).email ?? '')
      .trim()
      .toLowerCase();
    if (!email) return null;
    const name = (input as { name?: unknown }).name;
    return { email, ...(name ? { name: String(name) } : {}) };
  }
  if (typeof input !== 'string') return null;
  const trimmed = input.trim();
  if (!trimmed) return null;
  const angle = /^\s*(?:"?([^"<]*?)"?\s*)?<([^>]+)>\s*$/.exec(trimmed);
  if (angle) {
    const name = angle[1]?.trim();
    return { email: angle[2].trim().toLowerCase(), ...(name ? { name } : {}) };
  }
  return { email: trimmed.toLowerCase() };
}

/**
 * Parse an address list that may be a string, an array of strings, or an array
 * of `{email,name}` objects.
 */
export function parseAddressList(input: unknown): { emails: string[]; first?: ParsedAddress } {
  const out: ParsedAddress[] = [];

  const pushString = (value: string) => {
    // This intentionally supports the provider payloads we emulate, not full RFC parsing.
    for (const part of value.split(',')) {
      const parsed = parseAddress(part);
      if (parsed) out.push(parsed);
    }
  };

  if (typeof input === 'string') {
    pushString(input);
  } else if (Array.isArray(input)) {
    for (const entry of input) {
      if (typeof entry === 'string') {
        pushString(entry);
      } else if (entry && typeof entry === 'object' && 'email' in entry) {
        const email = String((entry as { email: unknown }).email ?? '')
          .trim()
          .toLowerCase();
        if (email) {
          const name = (entry as { name?: unknown }).name;
          out.push({ email, ...(name ? { name: String(name) } : {}) });
        }
      }
    }
  }

  return { emails: out.map((a) => a.email), ...(out[0] ? { first: out[0] } : {}) };
}

/** Decode a base64 payload to a UTF-8 string. */
export function decodeBase64(data: string): string {
  return Buffer.from(data, 'base64').toString('utf-8');
}

/** Decode quoted-printable: soft line breaks plus `=XX` hex escapes. */
function decodeQuotedPrintable(input: string): string {
  return input
    .replace(/=\r?\n/g, '')
    .replace(/=([0-9A-Fa-f]{2})/g, (_m, hex) => String.fromCharCode(parseInt(hex, 16)));
}

function decodeMimeBody(body: string, contentTransferEncoding: string): string {
  const cte = contentTransferEncoding.trim().toLowerCase();
  if (cte === 'base64') {
    return Buffer.from(body.replace(/\s+/g, ''), 'base64').toString('utf-8');
  }
  if (cte === 'quoted-printable') return decodeQuotedPrintable(body);
  return body;
}

/**
 * Best-effort raw-MIME parse: header block plus first body part. Enough to
 * surface OTP and magic-link mail without pulling in a full MIME parser.
 */
export function parseRawMime(raw: string): ParsedRawMime {
  const norm = raw.replace(/\r\n/g, '\n');
  const splitAt = norm.indexOf('\n\n');
  const headerBlock = splitAt >= 0 ? norm.slice(0, splitAt) : norm;
  const body = splitAt >= 0 ? norm.slice(splitAt + 2) : '';

  const unfolded = headerBlock.replace(/\n[ \t]+/g, ' ');
  const headers = new Map<string, string>();
  for (const line of unfolded.split('\n')) {
    const m = /^([^:]+):\s*(.*)$/.exec(line);
    if (m) headers.set(m[1].trim().toLowerCase(), m[2].trim());
  }

  const contentType = headers.get('content-type') ?? '';
  const isHtml = /text\/html/i.test(contentType);
  const isMultipart = /multipart\//i.test(contentType);
  const decodedBody = isMultipart
    ? body
    : decodeMimeBody(body, headers.get('content-transfer-encoding') ?? '');

  return {
    from: parseAddress(headers.get('from')),
    to: parseAddressList(headers.get('to')).emails,
    cc: parseAddressList(headers.get('cc')).emails,
    bcc: parseAddressList(headers.get('bcc')).emails,
    subject: headers.get('subject') ?? null,
    text: isHtml ? null : decodedBody || null,
    html: isHtml ? decodedBody || null : null,
  };
}

export function isRecord(value: unknown): value is JsonObject {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

export function safeParseJson(
  body: string | undefined
): { ok: true; data: unknown } | { ok: false } {
  try {
    return { ok: true, data: JSON.parse(body ?? '{}') };
  } catch {
    return { ok: false };
  }
}

export function extractGenericAttachments(
  input: unknown,
  nameKey: string,
  typeKey: string
): CapturedEmail['attachments'] {
  if (!Array.isArray(input)) return [];
  return input.map((entry) => {
    const att = (entry ?? {}) as Record<string, unknown>;
    const filename = att[nameKey];
    const contentType = att[typeKey];
    return {
      ...(typeof filename === 'string' ? { filename } : {}),
      ...(typeof contentType === 'string' ? { contentType } : {}),
    };
  });
}
